import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { api } from "../../lib/api";
import { queryKeys } from "../../lib/queryKeys";
import { shortDateTime } from "../../lib/time";
import { Button } from "../../components/ui/Button";
import { Panel } from "../../components/ui/Panel";
import { inputClass } from "./shared";

/**
 * The senders the spam filter is told to leave alone.
 *
 * This is the answer to a specific complaint, and the panel is shaped by it:
 * somebody has just found a supplier's order confirmation sitting in Spam for
 * the third time, and what they want is to type that address in and never think
 * about it again. So there is one box, it takes what is on their clipboard --
 * an address, or a domain, or a domain with the `@` still on the front -- and
 * the server works out which of those it was.
 *
 * There is no matching block list. Gmail's filter is far better at deciding
 * what junk is than a list anybody here would maintain; what it cannot know is
 * which of its own verdicts this desk disagrees with, and that is a short list
 * somebody adds to when they notice. See the table's header in schema.ts.
 *
 * Readable by every agent and writable only by an admin, so an agent looking at
 * something in Spam can check whether the sender is already allowed without
 * having to go and ask.
 */
export function SpamSettings({ isAdmin }: { isAdmin: boolean }) {
  const queryClient = useQueryClient();
  const [value, setValue] = useState("");
  const [note, setNote] = useState("");
  const [error, setError] = useState<string | null>(null);

  const allowed = useQuery({
    queryKey: queryKeys.allowedSenders,
    queryFn: api.allowedSenders,
  });

  const invalidate = () =>
    void queryClient.invalidateQueries({ queryKey: queryKeys.allowedSenders });

  const add = useMutation({
    mutationFn: () => api.addAllowedSender(value.trim(), note.trim() || null),
    onSuccess: () => {
      setValue("");
      setNote("");
      setError(null);
      invalidate();
    },
    /* The server's message names the actual problem -- a missing dot, two
       addresses in one box -- so it is shown rather than replaced with a
       generic failure. */
    onError: (failure: Error) => setError(failure.message),
  });

  const remove = useMutation({
    mutationFn: (id: number) => api.removeAllowedSender(id),
    onSuccess: invalidate,
  });

  const entries = allowed.data ?? [];

  return (
    <Panel collapsible title="Allowed senders">
      <p className="mb-3 text-xs text-muted">
        Mail from these senders is never filed as spam, whatever the filter
        thinks of it. Add one when something you needed to see keeps ending up
        in the Spam view.
      </p>

      {isAdmin ? (
        <form
          className="mb-4 flex flex-wrap items-start gap-2"
          onSubmit={(event) => {
            event.preventDefault();
            if (value.trim()) add.mutate();
          }}
        >
          <div className="min-w-[15rem] flex-1">
            <input
              className={`${inputClass} w-full`}
              placeholder="accounts@supplier.co.uk, or supplier.co.uk"
              value={value}
              onChange={(event) => {
                setValue(event.target.value);
                setError(null);
              }}
              aria-label="Email address or domain"
            />
            {error ? (
              <p className="mt-1 text-xs text-sla-breached">{error}</p>
            ) : null}
          </div>

          {/*
            The note is worth a box of its own rather than being left to a
            comment nobody can write. This list is read months later by somebody
            deciding whether an entry is still needed, and an address on its own
            does not answer that.
          */}
          <input
            className={`${inputClass} min-w-[12rem] flex-1`}
            placeholder="Why (optional)"
            value={note}
            onChange={(event) => setNote(event.target.value)}
            aria-label="Why this sender is allowed"
          />

          <Button type="submit" disabled={!value.trim() || add.isPending}>
            Allow
          </Button>
        </form>
      ) : null}

      {allowed.isPending ? (
        <p className="text-sm text-faint">Loading…</p>
      ) : entries.length === 0 ? (
        <p className="text-sm text-faint">
          Nothing allowed yet. The filter is deciding on its own.
        </p>
      ) : (
        <ul className="divide-y divide-line">
          {entries.map((entry) => (
            <li
              key={entry.id}
              className="flex items-baseline gap-3 py-2 text-sm"
            >
              <div className="min-w-0 flex-1">
                <span className="text-content">{entry.value}</span>
                {/*
                  Said out loud, because the difference matters: one of these
                  allows a person and the other allows everybody at a company.
                */}
                {entry.kind === "domain" ? (
                  <span className="ml-2 text-xs text-muted">
                    everyone at this domain
                  </span>
                ) : null}
                {entry.note ? (
                  <span className="block truncate text-xs text-muted">
                    {entry.note}
                  </span>
                ) : null}
              </div>

              <span className="hidden shrink-0 text-[11px] text-faint sm:inline">
                {entry.createdByName
                  ? `${entry.createdByName}, ${shortDateTime(entry.createdAt)}`
                  : shortDateTime(entry.createdAt)}
              </span>

              {isAdmin ? (
                <Button
                  variant="secondary"
                  className="shrink-0"
                  disabled={remove.isPending}
                  onClick={() => remove.mutate(entry.id)}
                >
                  Remove
                </Button>
              ) : null}
            </li>
          ))}
        </ul>
      )}
    </Panel>
  );
}
